import React, { useEffect } from 'react';
import { createState, useState, useHookstate } from '@hookstate/core';
import { getMarkersDispatchService } from '~/features/Dispatch/services';
import { getMarkersEntryService } from '~/features/Entry/services';
import { getMarkersDeviceAllService } from '~/features/Device/services';
import useDispatchState from '~/features/Dispatch/stores/DispatchState';
import useEntryState from '~/features/Entry/stores/EntryState';
import { useMap } from 'react-leaflet';
import { getRelativeDraggablePosition } from '~/utils/screen';
import { latLng, LatLng, LatLngBounds } from 'leaflet';
import { none } from '@hookstate/core'
import { createLiveOperation, getAreaPlanService, getMarkersCameraService, getMarkersInterestPointService } from '~/features/Map/services/request';
import useSystemState from '~/features/System/stores/SystemState';
import { Dayjs } from 'dayjs'
import { Coordinate } from 'bizcharts';
import type { IGeographical } from '../components/FilterGeographicalArea/interfaces';

interface ICreateMarkerProps {
  readonly markerType: any,
  readonly markerIdentifier: any,
  readonly id: any,
  readonly code?: any,
  readonly location?: any,
  readonly position?: any,
  readonly typeName?: any,
  readonly subtypeName?: any,
  readonly status?: any,
  readonly isCurrent?: any,
  readonly identifier?: any,
  readonly speed?: any,
  readonly plate?: any,
  readonly date?: any,
  readonly groupId?: any,
  readonly deviceType?: any,
  readonly dispatchedAt?: any,
  readonly uniqueReference?: boolean,
  readonly agencyName?: any
  readonly incidentId?: any
  readonly isCriminal?: any
  readonly validatedAt?: any
  readonly revisionDate?: any
  readonly expirationDate?: any
  readonly nameInterestPoint?: any
  readonly medias?: any
  readonly notes?: any
}

interface IBoundCenterActualStateProps {
  readonly lat: number | null,
  readonly lng: number | null,
}

const markersDefault = {
  occurrence: {},
  entry: {},
  dispatch: {},
  device: {},
  camera: {},
  interestPoint: {},
  prediction: {},
  areas: {},
  dispatchFiltered: {},
  deviceHistoric: {},
  dispatchHistoric: {},
  vehiclePassage: {},
  locationRequester: {},
  dispatchHeatMap: {}
}

const zoomDefault = 15

const markersLocal = createState(markersDefault);

const forceViewMapState = createState({
  position: new LatLng(0, 0),
  zoom: zoomDefault
});

const boundCenterActualState = createState<IBoundCenterActualStateProps>({
  lat: null,
  lng: null
})

const boundsMapGlobalState = createState<LatLngBounds | null>(null)
const forceClosePopupState = createState(false)
const dateTimeHistoricState = createState<Dayjs | null>(null)
const popupMarkerState = createState<any | null>(null)
const loadingRouteDeviceState = createState(false)
const geographicalAreaState = createState<readonly IGeographical[]>([])


export function useMarkerState() {
  const markersState = useState(markersLocal);
  const forceViewMap = useState(forceViewMapState);
  const boundCenterActual = useState(boundCenterActualState);
  const boundsMapGlobal = useState(boundsMapGlobalState);
  const forceClosePopup = useState(forceClosePopupState);
  const dateTimeHistoric = useState(dateTimeHistoricState);
  const popupMarker = useState(popupMarkerState)
  const loadingRouteDevice = useState(loadingRouteDeviceState)
  const geographicalArea = useState(geographicalAreaState)


  const deleteAllUniqueMarkerFunction = (markerType) => {
    Object.keys(markersState[markerType].get()).map((allMarkerID) => {
      if (markersState[markerType][allMarkerID].get()?.uniqueReference) {
        markersState[markerType][allMarkerID].set(none)
      }
    })
  }

  return ({
    markersState: () => markersState,
    forceViewMap: () => forceViewMap,
    boundCenterActual: () => boundCenterActual,
    boundsMapGlobal: () => boundsMapGlobal,
    forceClosePopup: forceClosePopup,
    dateTimeHistoric: dateTimeHistoric,
    popupMarker: popupMarker,
    loadingRouteDevice: loadingRouteDevice,
    geographicalArea: () => geographicalArea,
    createMarker: ({ markerType, markerIdentifier, id, code = null, location, position, typeName = null, subtypeName = null, status, isCurrent = false, identifier = null, speed = null, plate = null, date = null, groupId = null, deviceType = null, dispatchedAt = null, agencyName, incidentId = null, isCriminal = null, validatedAt = null, revisionDate = null, expirationDate = null, nameInterestPoint = null, medias = null, notes = null }: ICreateMarkerProps) => {
      if (document.getElementById('LeafletMapContainer')) {
        return markersState[markerType].merge({
          [markerIdentifier]: {
            id: id,
            markerId: `${markerType}-${id}`,
            code: code,
            location: location,
            position: position,
            typeName: typeName,
            subtypeName: subtypeName,
            status: status,
            isCurrent: isCurrent,
            identifier: identifier,
            speed: speed,
            plate: plate,
            date: date,
            groupId: groupId,
            deviceType: deviceType,
            dispatchedAt: dispatchedAt,
            agencyName: agencyName,
            incidentId: incidentId,
            isCriminal: isCriminal,
            validatedAt: validatedAt,
            revisionDate: revisionDate,
            expirationDate: expirationDate,
            nameInterestPoint: nameInterestPoint,
            medias: medias,
            notes: notes
          }
        });
      }
    },
    updateMarker: (markerTypeOrParams: string | { readonly markerType: string; readonly markerIdentifier: any; readonly attribute: string; readonly value: any }, markerIdentifier?: any, updates?: Record<string, any>) => {
      if (document.getElementById('LeafletMapContainer')) {
        try {
          // Suporta ambas as assinaturas: antiga e nova
          if (typeof markerTypeOrParams === 'string') {
            // Nova assinatura: updateMarker(markerType, markerIdentifier, updates)
            const marker = markersState[markerTypeOrParams][markerIdentifier];
            if (!marker) return false;
            
            // Atualiza múltiplos atributos de uma vez
            if (updates) {
              Object.keys(updates).forEach((attribute) => {
                if (marker[attribute]) {
                  marker[attribute].set(updates[attribute]);
                }
              });
            }
            
            return true;
          } else {
            // Assinatura antiga: updateMarker({ markerType, markerIdentifier, attribute, value })
            const { markerType, markerIdentifier: id, attribute, value } = markerTypeOrParams;
            return markersState[markerType][id] && markersState[markerType][id][attribute] 
              ? markersState[markerType][id][attribute].set(value) 
              : false;
          }
        } catch (error) {
          console.error(error)
          throw error
        }
      }
    },
    deleteMarker: (markerTypeOrParams: string | { readonly markerType: string; readonly markerIdentifier: any }, markerIdentifier?: any) => {
      if (document.getElementById('LeafletMapContainer')) {
        // Suporta ambas as assinaturas: antiga e nova
        if (typeof markerTypeOrParams === 'string') {
          // Nova assinatura: deleteMarker(markerType, markerIdentifier)
          return markersState[markerTypeOrParams][markerIdentifier] 
            ? markersState[markerTypeOrParams][markerIdentifier].set(none) 
            : false;
        } else {
          // Assinatura antiga: deleteMarker({ markerType, markerIdentifier })
          const { markerType, markerIdentifier: id } = markerTypeOrParams;
          return markersState[markerType][id] 
            ? markersState[markerType][id].set(none) 
            : false;
        }
      }
    },
    getMarker: ({ markerType, markerIdentifier }) => {
      if (document.getElementById('LeafletMapContainer')) {
        try {
          const markerState = markersState[markerType];
          if (!markerState) return null;

          return markerState[markerIdentifier];
        } catch (error) {
          console.error(error)
          throw error
        }
      }
    },
    reloadInterestPointMarkers: () => {
      if (document.getElementById('LeafletMapContainer')) {
        getMarkersInterestPointService().then((response) => {
          markersState['interestPoint'].set(response)
        })
      }
    },
    deleteAllUniqueMarker: (markerType) => deleteAllUniqueMarkerFunction(markerType),
    createUniqueMarker: ({ uniqueReference, markerType, markerIdentifier, id, code = null, location, position, typeName = null, subtypeName = null, status, isCurrent = false, identifier = null, speed = null, plate = null, date = null, groupId = null, deviceType = null, dispatchedAt = null, isCriminal = null, validatedAt = null, revisionDate = null, expirationDate = null, nameInterestPoint = null, medias = null, notes = null }: ICreateMarkerProps) => {
      if (document.getElementById('LeafletMapContainer')) {
        deleteAllUniqueMarkerFunction(markerType);

        return markersState[markerType].merge({
          [markerIdentifier]: {
            id: id,
            markerId: `${markerType}-${id}`,
            code: code,
            location: location,
            position: position,
            typeName: typeName,
            subtypeName: subtypeName,
            status: status,
            isCurrent: isCurrent,
            identifier: identifier,
            speed: speed,
            plate: plate,
            date: date,
            groupId: groupId,
            deviceType: deviceType,
            dispatchedAt: dispatchedAt,
            uniqueReference: uniqueReference,
            isCriminal: isCriminal,
            validatedAt: validatedAt,
            revisionDate: revisionDate,
            expirationDate: expirationDate,
            nameInterestPoint: nameInterestPoint,
            medias: medias,
            notes: notes
          }
        });
      }
    }
  })
}

export const validShowArea = ({ position, boundsMap }) => {
  if (position && boundsMap) {
    if (position?.lng <= boundsMap.getNorthEast().lng && position?.lng >= boundsMap.getSouthWest().lng) {
      if (position?.lat <= boundsMap.getNorthEast().lat && position?.lat >= boundsMap.getSouthWest().lat) {
        return true
      }
    }
  }
  return false
}

export default function useMapState() {
  const map = useMap();
  const { dispatch } = useDispatchState();
  const { entry } = useEntryState();
  const markersState = useMarkerState().markersState();
  // const { forceViewMap, boundCenterActual } = useMarkerState();
  const { forceViewMap, boundCenterActual, boundsMapGlobal } = useMarkerState();
  const boundsMap = useHookstate<LatLngBounds | null>(null);
  const zoomMapActual = useHookstate<number>(0);
  const { windowManager } = useSystemState();

  useEffect(() => {
    if (boundsMap.get()) {
      boundCenterActual().lat.set(map.getCenter().lat);
      boundCenterActual().lng.set(map.getCenter().lng);
    }
  }, [boundsMap.get(), zoomMapActual.get()])

  useEffect(() => {
    zoomMapActual.set(map.getZoom())
  }, [map.getZoom()])

  const setViewMap = ({ position, zoom }) => {
    if (document.getElementById('LeafletMapContainer')) {
      const getBoundByDraggablePosition = ({ xAxis, yAxis, bound }) => {
        if (yAxis === 1) {
          if (xAxis === -1) {
            return new LatLngBounds(bound.getSouthWest(), bound.getCenter()).getCenter();
          }
          if (xAxis === 1) {
            return new LatLngBounds(new LatLng(bound.getSouthWest().lat, bound.getCenter().lng), new LatLng(bound.getCenter().lat, bound.getNorthEast().lng)).getCenter();
          }
        } else {
          if (xAxis === -1) {
            return new LatLngBounds(new LatLng(bound.getCenter().lat, bound.getSouthWest().lng), new LatLng(bound.getNorthEast().lat, bound.getCenter().lng)).getCenter();
          }
          if (xAxis === 1) {
            return new LatLngBounds(bound.getCenter(), bound.getNorthEast()).getCenter();
          }
        }
      };
      const [xAxis, yAxis] = document.querySelector('.react-draggable') ? getRelativeDraggablePosition() : [map.getCenter().lat, map.getCenter().lng];
      const mapUpdatedTemp = map.setView(position, zoom, { animate: false })
      const getRectangleBound = getBoundByDraggablePosition({ xAxis: xAxis, yAxis: yAxis, bound: mapUpdatedTemp.getBounds() });
      return map.setView(getRectangleBound ? getRectangleBound : position, zoom, { animate: true })
    }
  }

  const loadMarkersState = useHookstate({
    entry: () => getMarkersEntryService().then((response) => {
      const entryActual = entry().id.get()
      if (entryActual && response[entryActual]) {
        response[entryActual].isCurrent = true;
        setViewMap({ position: response[entryActual].position, zoom: zoomDefault });
        // boundCenterActual().set({lat: map.getCenter().lat, lng: map.getCenter().lng})
      }

      markersState['entry'].set(response)
    }),
    dispatch: () => getMarkersDispatchService().then((response) => {
      const dispatchActual = dispatch().id.get();
      if (dispatchActual && response[dispatchActual]) {
        response[dispatchActual].isCurrent = true;
        setViewMap({ position: response[dispatchActual].position, zoom: zoomDefault });
        // boundCenterActual().set({lat: map.getCenter().lat, lng: map.getCenter().lng})
      }

      markersState['dispatch'].set(response);
    }),
    device: (abort) => getMarkersDeviceAllService({ abort: abort }).then((response) => {
      if (response) {
        markersState['device'].set(response);
      }
    }),
    camera: () => getMarkersCameraService().then((response) => {
      markersState['camera'].set(response);
    }),
    occurrence: () => {
      console.info('Carregando Ocorrencias')
    },
    interestPoint: () => getMarkersInterestPointService().then((response) => {
      markersState['interestPoint'].set(response)
    }),
    prediction: () => console.info('Carregando Predições'),
    dispatchFiltered: () => console.info('Carregando Predições de Despachos'),
    deviceHistoric: () => console.info('Carregando Histórico de Dispositivos'),
    dispatchHistoric: () => console.info('Carregando Histórico de Despachos'),
    vehiclePassage: () => console.info('Carregando Passagens de Veículos Roubados/Furtados'),
    locationRequester: () => { console.info('Carregando Integração Uber') },
    areas: () => {
      windowManager()['mapSetting']['markersView']['areas'].merge({ hide: true, show: false })
    },
    dispatchHeatMap: () => console.info('Carregando Heat Map de Despachos'),
  })

  useEffect(() => {
    if (forceViewMap().position.get().lng != 0 && forceViewMap().position.get().lat != 0) {
      setViewMap({ position: forceViewMap().position.get(), zoom: forceViewMap().zoom.get() });
      forceViewMap().position.set(new LatLng(0, 0));
      forceViewMap().zoom.set(15)
      boundsMap.set(map.getBounds())
      zoomMapActual.set(map.getZoom())
    }
  }, [forceViewMap().position.get()])

  return ({
    map: () => map,
    setViewMap: setViewMap,
    boundsMap: () => boundsMap,
    zoomMapActual: () => zoomMapActual,
    markers: () => markersState,
    validShowArea: () => validShowArea,
    loadMarkersState: (markerType, abort, latitude, longitude,) => {
      const markers = loadMarkersState.value;
      return markers[markerType](abort, latitude, longitude)
    },
  })
}
